import { useState } from 'react';
import { Card, Group, Loader, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { staffPath, useSearch } from '@/api/ops';
import { EmptyState } from '@/components/EmptyState';
import { objectName } from '@/features/objects/objectName';
import { brand } from '@/theme/theme';

/** The staff top-bar box: Enter takes the typed term to the results page. */
export function GlobalSearch() {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <TextInput
      size="xs"
      w={200}
      miw={110}
      aria-label={t('search.label')}
      placeholder={t('search.placeholder')}
      value={q}
      onChange={(e) => setQ(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && q.trim().length >= 2) void navigate({ to: '/staff/search', search: { q: q.trim() } });
      }}
    />
  );
}

interface Row {
  key: string;
  link: string;
  title: string;
  detail: string;
}

function Section({ label, rows }: { label: string; rows: Row[] }) {
  if (rows.length === 0) return null;
  return (
    <Card p={0}>
      <Group px="md" py={8} bg={brand.surface2}>
        <Text fz={13} fw={700}>
          {label}
        </Text>
      </Group>
      {rows.map((row) => (
        <Group key={row.key} px="md" py={8} justify="space-between" style={{ borderTop: `1px solid ${brand.border}` }}>
          {row.link ? (
            <Link to={staffPath(row.link)} style={{ color: brand.text, fontWeight: 600, textDecoration: 'none' }}>
              {row.title}
            </Link>
          ) : (
            <Text fz={14} fw={600}>
              {row.title}
            </Text>
          )}
          <Text fz={12} c={brand.textMuted}>
            {row.detail}
          </Text>
        </Group>
      ))}
    </Card>
  );
}

/** Results by family for one search term; each row opens the staff page for that thing. */
export function SearchResults({ q }: { q: string }) {
  const { t } = useTranslation('ops');
  const results = useSearch(q);

  if (q.trim().length < 2) return <EmptyState title={t('search.hint')} />;
  if (results.isPending) return <Loader color="forest" />;
  if (results.isError) return <EmptyState title={errorMessage(results.error)} />;

  const r = results.data;
  const total = r.projects.length + r.organizations.length + r.objects.length + r.analyses.length + r.runs.length + r.reports.length + r.users.length;
  if (total === 0) return <EmptyState title={t('search.empty')} description={t('search.hint')} />;

  return (
    <Stack gap="md">
      <Title order={1}>{t('search.title')}</Title>
      <Section
        label={t('search.projects')}
        rows={r.projects.map((p) => ({ key: p.public_id, link: p.link, title: p.name, detail: p.organization }))}
      />
      <Section label={t('search.organizations')} rows={r.organizations.map((o) => ({ key: o.public_id, link: o.link, title: o.name, detail: o.public_id }))} />
      <Section
        label={t('search.objects')}
        rows={r.objects.map((o) => ({ key: o.public_id, link: o.link, title: objectName(o), detail: `${o.kind} · ${o.project}` }))}
      />
      <Section label={t('search.analyses')} rows={r.analyses.map((a) => ({ key: a.public_id, link: a.link, title: a.name, detail: `${a.type_key} · ${a.project}` }))} />
      <Section
        label={t('search.runs')}
        rows={r.runs.map((x) => ({
          key: x.public_id,
          link: x.link,
          title: x.external_id ? `${x.public_id} · forun #${x.external_id}` : x.public_id,
          detail: `${x.type_key} · ${x.state} · ${x.project}`,
        }))}
      />
      <Section label={t('search.reports')} rows={r.reports.map((x) => ({ key: x.public_id, link: x.link, title: x.name || x.public_id, detail: `${x.kind} · ${x.project}` }))} />
      <Section label={t('search.users')} rows={r.users.map((u) => ({ key: u.public_id, link: u.link, title: u.full_name || u.email, detail: `${u.email} · ${u.organization}` }))} />
    </Stack>
  );
}
